import React from "react";
import { Box, Button, CardContent, Typography } from "@mui/material";
import {
  Login,
  useAuthProvider,
  useNotificationContext,
  useNotify
} from "react-admin";
import { useLocation, useNavigate } from "react-router-dom";
import { Authenticator } from "@aws-amplify/ui-react";

import "@aws-amplify/ui-react/styles.css";
import "./LoginPage.css";
import { loginWithDuo } from "../providers/auth/loginWithDuo";
import {
  PASSWORD_POLICY,
  PASSWORD_REQUIREMENTS,
  PASSWORD_REQUIREMENTS_HEADING
} from "../providers/auth/passwordPolicy";

/**
 * Post-login redirect.
 *
 * <Authenticator> renders its children only once Cognito has actually issued
 * tokens — after a plain sign-in, or after a challenge screen such as
 * force-new-password finishes — so a child is the app's "auth complete" signal.
 * The redirect has to live here rather than in handleSignIn: react-admin's
 * useLogin navigates the moment authProvider.login resolves, and Amplify v6
 * resolves signIn on a challenge too. That premature navigation makes
 * requireAuth bounce straight back to /login, remounting this page and
 * discarding the challenge step the Authenticator was about to render.
 *
 * Mirrors useLogin's own redirect (ra-core/auth/useLogin) so a deep link
 * followed by a login still lands on the requested route.
 */
const PostLoginRedirect = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { resetNotifications } = useNotificationContext();
  const hasRedirected = React.useRef(false);

  React.useEffect(() => {
    // Redirect once only; a later render must not navigate a second time.
    if (hasRedirected.current) return;
    hasRedirected.current = true;

    const { nextPathname, nextSearch } = (state ?? {}) as {
      nextPathname?: string;
      nextSearch?: string;
    };
    resetNotifications();
    navigate(nextPathname ? `${nextPathname}${nextSearch ?? ""}` : "/");
  }, [navigate, state, resetNotifications]);

  return null;
};

/**
 * The pool's password policy, spelled out on the change-password screen. Without
 * it a rejected password reads as an arbitrary refusal — the length requirement
 * is well above Cognito's default, and the breach check cannot be predicted.
 */
const PasswordRequirements = () => (
  <Box data-cy="password-requirements" sx={{ mt: 1 }}>
    <Typography variant="body2" color="text.secondary">
      {PASSWORD_REQUIREMENTS_HEADING}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      component="ul"
      sx={{ pl: 3, m: 0 }}
    >
      {PASSWORD_REQUIREMENTS.map((requirement) => (
        <li key={requirement}>{requirement}</li>
      ))}
    </Typography>
  </Box>
);

/**
 * Sign-in screen.
 *
 * The form body is Amplify's <Authenticator>, wrapped in react-admin's <Login>
 * for the card and background. Two deliberate omissions:
 *
 * - `hideSignUp`: there is no self-service registration. Members are invited by
 *   an admin (see components/resources/member/MemberInvite.tsx).
 * - Overriding SignIn.Footer replaces Amplify's stock "Forgot your password?"
 *   link, since password reset is not wired up in this app.
 */
const LoginPage = () => {
  const authProvider = useAuthProvider();
  const notify = useNotify();

  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  const handleLoginError = (error: any) => {
    notify(error.message, { type: "error" });
  };

  const handleDuoLogin = () => {
    loginWithDuo().catch(handleLoginError);
  };

  /**
   * Sign-in override for the Authenticator, so the app has a single auth path.
   *
   * Calls authProvider.login directly rather than react-admin's useLogin: login
   * returns Amplify's SignInOutput untouched, which is exactly what Amplify's
   * state machine needs to route to a challenge screen (force-new-password,
   * MFA) when isSignedIn is false. Don't call Amplify's signIn here as well.
   * Redirecting is PostLoginRedirect's job, not this function's.
   */
  const handleSignIn = (input: { username: string; password?: string }) =>
    authProvider.login({
      ...input,
      username: input.username.trim().toLowerCase(),
      password: input.password || ""
    });

  const duoFooter = () => (
    <>
      <Box sx={{ display: "flex", alignItems: "center", mt: 2, mb: 2 }}>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e0e0e0" }} />
        <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
          or
        </Typography>
        <Box sx={{ flex: 1, height: "1px", backgroundColor: "#e0e0e0" }} />
      </Box>
      <Button
        variant="outlined"
        fullWidth
        onClick={handleDuoLogin}
        data-cy="duo-sign-in-button"
        sx={{
          textTransform: "none",
          borderColor: "#e0e0e0",
          "&:hover": {
            borderColor: "#bdbdbd",
            backgroundColor: "#f5f5f5"
          }
        }}
      >
        <Typography variant="body2" sx={{ mr: 1, color: "text.secondary" }}>
          Login with
        </Typography>
        <img src="duo-logo.png" alt="Duo" height="30px" />
      </Button>
    </>
  );

  return (
    <Login
      backgroundImage="strivestudy_login.png"
      sx={{
        backgroundPosition: "center",
        "& .RaLogin-card": {
          marginTop: "16em",
          boxShadow:
            "0px 3px 14px 2px rgba(0, 0, 0, 0.12), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 5px 5px -3px rgba(0, 0, 0, 0.2)"
        }
      }}
    >
      <CardContent sx={{ width: "300px" }}>
        <Authenticator
          hideSignUp
          formFields={{
            signIn: {
              username: {
                label: "Email",
                placeholder: "Enter your email",
                autocomplete: "username"
              },
              password: {
                label: "Password",
                placeholder: "Enter your password",
                autocomplete: "current-password"
              }
            },
            // Shown when Cognito answers sign-in with
            // CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED, i.e. a user still in
            // FORCE_CHANGE_PASSWORD. Amplify supplies the screen and calls
            // confirmSignIn itself; only the labels are ours.
            forceNewPassword: {
              password: {
                label: "New password",
                placeholder: "Enter a new password",
                autocomplete: "new-password"
              },
              confirm_password: {
                label: "Confirm new password",
                placeholder: "Re-enter the new password",
                autocomplete: "new-password"
              }
            }
          }}
          // Checks the length locally, in Amplify's own wording, instead of
          // waiting for Cognito to reject it.
          passwordSettings={PASSWORD_POLICY}
          services={{ handleSignIn }}
          components={{
            SignIn: { Footer: duoFooter },
            // Default Footer renders nothing, so nothing is lost here.
            ForceNewPassword: { Footer: PasswordRequirements }
          }}
        >
          {() => <PostLoginRedirect />}
        </Authenticator>
      </CardContent>
    </Login>
  );
};

export default LoginPage;
